"use client"

import React, { useState, useContext, useEffect, useMemo } from "react"
import { Link, useLocation } from "react-router-dom"
import { ErpContext } from "../../context/ErpContext"
import { useTenantPath } from "../../hooks/useTenantNavigation"
import Cookies from "js-cookie"
import { ordenarEstructuraMenu } from "../../utils/menuAccesoOrden"

// Material UI
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Avatar,
  Typography,
  IconButton,
  Tooltip,
  Badge,
  useTheme,
  alpha,
  Button,
  useMediaQuery,
} from "@mui/material"

// Icons
import HomeIcon from "@mui/icons-material/Home"
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn"
import ProductionQuantityLimitsIcon from "@mui/icons-material/ProductionQuantityLimits"
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted"
import AddCircleIcon from "@mui/icons-material/AddCircle"
import ViewListIcon from "@mui/icons-material/ViewList"
import PermDataSettingIcon from "@mui/icons-material/PermDataSetting"
import AddLocationAltIcon from "@mui/icons-material/AddLocationAlt"
import PeopleAltIcon from "@mui/icons-material/PeopleAlt"
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange"
import LocalAtmIcon from "@mui/icons-material/LocalAtm"
import RequestQuoteIcon from "@mui/icons-material/RequestQuote"
import CardTravelIcon from "@mui/icons-material/CardTravel"
import PaymentsIcon from "@mui/icons-material/Payments"
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart"
import ExpandLess from "@mui/icons-material/ExpandLess"
import ExpandMore from "@mui/icons-material/ExpandMore"
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft"
import LogoutIcon from "@mui/icons-material/Logout"
import InventoryIcon from "@mui/icons-material/Inventory"
import ReceiptIcon from "@mui/icons-material/Receipt"
import PersonIcon from "@mui/icons-material/Person"
import CloseIcon from "@mui/icons-material/Close"
import SupportAgentIcon from "@mui/icons-material/SupportAgent"
import BadgeIcon from "@mui/icons-material/Badge"
import ScheduleIcon from "@mui/icons-material/Schedule"
import FingerprintIcon from "@mui/icons-material/Fingerprint"
import FactCheckIcon from "@mui/icons-material/FactCheck"
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong"
import AssessmentIcon from "@mui/icons-material/Assessment"

// Imágenes
import imgLutenteNav from "../../assets/images/LutenteNavTransparent.png"
import lutenteMini from "../../assets/images/lutenteMini.png"

const drawerWidth = 280

const tieneAcceso = (id, accesos) => {
  return accesos?.some((a) => a.idMenuAcceso === id && a.eliminado === false)
}

const NavBar = () => {
  const theme = useTheme()
  const isTabletDown = useMediaQuery(theme.breakpoints.down("lg"))
  const location = useLocation()
  const { user, logout, setIsNavbarCollapsed, isNavbarCollapsed, getMenuAcceso } = useContext(ErpContext)
  const buildPath = useTenantPath()

  // Estado para Drawer móvil
  const [mobileOpen, setMobileOpen] = useState(false)

  // Estados para gestos de swipe
  const [touchStart, setTouchStart] = useState({ x: null, y: null })
  const [touchEnd, setTouchEnd] = useState({ x: null, y: null })
  const minSwipeDistance = 50 // Distancia mínima para considerar un swipe

  // Estado para los menús expandidos
  const [openMenus, setOpenMenus] = useState({
    VENTAS: false,
    INVENTARIO: false,
    COMPRAS: false,
    TRANSACCION: false,
    RRHH: false,
    ADMINISTRACION: false,
    SOPORTE: false,
  })

  // Estado para el elemento activo
  const [activeItem, setActiveItem] = useState("")

  // Estado para las descripciones de menuAcceso
  const [menuDescripciones, setMenuDescripciones] = useState({})

  // Funciones para manejar gestos de swipe
  const onTouchStart = (e) => {
    setTouchEnd({ x: null, y: null })
    setTouchStart({
      x: e.targetTouches[0].clientX,
      y: e.targetTouches[0].clientY
    })
  }

  const onTouchMove = (e) => {
    setTouchEnd({
      x: e.targetTouches[0].clientX,
      y: e.targetTouches[0].clientY
    })
  }

  const onTouchEnd = () => {
    // Si no hay valores válidos de touchEnd, significa que no hubo movimiento (es un click)
    if (!touchStart.x || !touchEnd.x || !touchEnd.y) {
      // Resetear y permitir que el click normal funcione
      setTouchStart({ x: null, y: null })
      setTouchEnd({ x: null, y: null })
      return
    }
    
    const distanceX = touchStart.x - touchEnd.x
    const distanceY = touchStart.y - touchEnd.y
    
    // Calcular la distancia total del movimiento
    const totalDistance = Math.sqrt(distanceX * distanceX + distanceY * distanceY)
    
    // Si el movimiento es muy pequeño (menos de 10px), es un tap/click, no un swipe
    if (totalDistance < 10) {
      setTouchStart({ x: null, y: null })
      setTouchEnd({ x: null, y: null })
      return
    }
    
    // Calcular si el movimiento es más horizontal que vertical
    const isHorizontalSwipe = Math.abs(distanceX) > Math.abs(distanceY)
    
    // Solo procesar si es un swipe horizontal predominante
    if (!isHorizontalSwipe) {
      setTouchStart({ x: null, y: null })
      setTouchEnd({ x: null, y: null })
      return
    }
    
    const isLeftSwipe = distanceX > minSwipeDistance
    const isRightSwipe = distanceX < -minSwipeDistance
    
    // Swipe desde el borde izquierdo hacia la derecha abre el navbar
    if (isRightSwipe && touchStart.x < 50) {
      setMobileOpen(true)
    }
    
    // Swipe hacia la izquierda cierra el navbar
    if (isLeftSwipe && mobileOpen) {
      setMobileOpen(false)
    }
    
    // Resetear estados
    setTouchStart({ x: null, y: null })
    setTouchEnd({ x: null, y: null })
  }

  // Efecto para cargar las descripciones de menuAcceso
  useEffect(() => {
    const cargarMenuDescripciones = async () => {
      try {
        const menusData = await getMenuAcceso()
        console.log(menusData)
        if (menusData) {
          // Crear un objeto con id como clave y descripcion como valor
          const descripciones = {}
          menusData.forEach(menu => {
            descripciones[menu.id] = menu.descripcion
          })
          setMenuDescripciones(descripciones)
        }
      } catch (error) {
        console.error("Error al cargar descripciones de menú:", error)
      }
    }

    cargarMenuDescripciones()
  }, [])

  // Efecto para cerrar menús cuando se hace clic fuera del navbar colapsado
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Solo aplicar en modo colapsado (desktop) y cuando hay menús abiertos
      if (isNavbarCollapsed && !isTabletDown && Object.values(openMenus).some(open => open)) {
        // Verificar si el clic fue dentro del navbar o dropdown
        const navbarElement = document.querySelector('[role="navigation"]')
        const dropdownElements = document.querySelectorAll('[data-dropdown="true"]')
        
        let clickedInsideNavbarOrDropdown = false
        
        // Verificar si el clic fue dentro del navbar
        if (navbarElement && navbarElement.contains(event.target)) {
          clickedInsideNavbarOrDropdown = true
        }
        
        // Verificar si el clic fue dentro de algún dropdown flotante
        dropdownElements.forEach(dropdown => {
          if (dropdown.contains(event.target)) {
            clickedInsideNavbarOrDropdown = true
          }
        })
        
        // Si el clic fue fuera del navbar y fuera de los dropdowns, cerrar todos los menús
        if (!clickedInsideNavbarOrDropdown) {
          setOpenMenus({
            VENTAS: false,
            INVENTARIO: false,
            COMPRAS: false,
            TRANSACCION: false,
            RRHH: false,
            ADMINISTRACION: false,
            SOPORTE: false,
          })
        }
      }
    }

    // Agregar event listener
    document.addEventListener('mousedown', handleClickOutside)
    
    // Cleanup
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isNavbarCollapsed, isTabletDown, openMenus])

  // Efecto para determinar el elemento activo basado en la ruta actual. esto es solo visual. *****
  useEffect(() => {
    const path = location.pathname

    // Mapeo de rutas a elementos del menú
    const routeToMenuItem = {
      "/": "DASHBOARD",
      "/gestionGenerarVenta": "VENTAS1",
      "/tablaVentas": "VENTAS2",
      "/gestionGenerarPresupuesto": "VENTAS3",
      "/tablaPresupuesto": "VENTAS4",
      "/gestionCliente": "VENTAS7",
      "/ventaCrear": "VENTAS10",
      "/preventaCrear": "VENTAS11",
      "/tablaPreventas": "VENTAS12",
      "/INVENTARIO3": "INVENTARIO3",
      "/INVENTARIO4": "INVENTARIO4",
      "/itemProveedor": "itemProveedor",
      "/COMPRAS5": "COMPRAS5",
      "/tablaListaCosto": "COMPRAS5",
      "/tablaListaPrecio": "VENTAS8",
      "/INVENTARIO1": "INVENTARIO1",
      "/crearCompra": "COMPRAS1",
      "/tablaCompra": "COMPRAS2",
      "/gestionMoneda": "ADMINISTRACION1",
      "/gestionMedioPago": "TRANSACCIONES1",
      "/gestionTipoTransaccion": "TRANSACCIONES2",
      "/Impuestos": "ADMINISTRACION2",
      "/gestionCategoria": "ADMINISTRACION3",
      "/gestionSubCategoria": "ADMINISTRACION4",
      "/tablaTransaccionComponent": "TRANSACCIONES5",
      "/tablaCuentaCorriente": "TRANSACCIONES6",
      "/gestionUbicaciones": "ADMINISTRACION5",
      "/gestionEntidad": "ADMINISTRACION6",
      "/gestionUsuarios": "ADMINISTRACION7",
      "/TablaEntidadProveedor": "COMPRAS4",
      "/tablaVentasAfip": "TRANSACCIONES7",
      "/cajaComponentAnaliticos": "TRANSACCIONES8",
      "/cajaComponent": "TRANSACCIONES10",
      "/recetaComponent": "INVENTARIO2",
      "/tablaReceta": "INVENTARIO5",
      "/gestionGastosComponent": "TRANSACCIONES4",
      "/gestionarAccesos": "ADMINISTRACION8",
      "/tablaGastosComponent": "TRANSACCIONES9",
      "/tablaVentasEstados": "VENTAS9",
      "/rrhh/empleados": "RRHH1",
      "/rrhh/turnos": "RRHH2",
      "/rrhh/fichajes": "RRHH3",
      "/rrhh/asistencia": "RRHH4",
      "/rrhh/liquidaciones": "RRHH5",
      "/rrhh/reportes": "RRHH6",
      "/soporte/crear": "SOPORTE1",
      "/soporte/tickets": "SOPORTE2",
      "/soporte/ticket": "SOPORTE2",
      "/soporte/bandejas": "SOPORTE3",
      "/soporte/metricas": "SOPORTE4",
      "/soporte/configuracion": "SOPORTE5",
    }

    // Mapeo de elementos del menú a sus secciones padre
    const menuItemToSection = {
      VENTAS1: "VENTAS",
      VENTAS2: "VENTAS",
      VENTAS3: "VENTAS",
      VENTAS4: "VENTAS",
      VENTAS5: "VENTAS",
      VENTAS6: "VENTAS",
      VENTAS8: "VENTAS",
      VENTAS9: "VENTAS",
      VENTAS7: "VENTAS",
      VENTAS10: "VENTAS",
      VENTAS11: "VENTAS",
      VENTAS12: "VENTAS",
      INVENTARIO3: "INVENTARIO",
      INVENTARIO4: "INVENTARIO",
      INVENTARIO1: "INVENTARIO",
      itemProveedor: "INVENTARIO",
      INVENTARIO2: "INVENTARIO",
      INVENTARIO5: "INVENTARIO",
      COMPRAS1: "COMPRAS",
      COMPRAS2: "COMPRAS",
      COMPRAS3: "COMPRAS",
      COMPRAS5: "COMPRAS",
      COMPRAS4: "COMPRAS",
      TRANSACCIONES1: "TRANSACCION",
      TRANSACCIONES2: "TRANSACCION",
      TRANSACCIONES5: "TRANSACCION",
      TRANSACCIONES6: "TRANSACCION",
      TRANSACCIONES7: "TRANSACCION",
      TRANSACCIONES8: "TRANSACCION",
      TRANSACCIONES9: "TRANSACCION",
      TRANSACCIONES10: "TRANSACCION",
      RRHH1: "RRHH",
      RRHH2: "RRHH",
      RRHH3: "RRHH",
      RRHH4: "RRHH",
      RRHH5: "RRHH",
      RRHH6: "RRHH",
      ADMINISTRACION1: "ADMINISTRACION",
      ADMINISTRACION2: "ADMINISTRACION",
      ADMINISTRACION3: "ADMINISTRACION",
      ADMINISTRACION4: "ADMINISTRACION",
      ADMINISTRACION5: "ADMINISTRACION",
      ADMINISTRACION6: "ADMINISTRACION",
      ADMINISTRACION7: "ADMINISTRACION",
      ADMINISTRACION8: "ADMINISTRACION",
      SOPORTE1: "SOPORTE",
      SOPORTE2: "SOPORTE",
      SOPORTE3: "SOPORTE",
      SOPORTE4: "SOPORTE",
      SOPORTE5: "SOPORTE",
    }

    const menuItem = routeToMenuItem[path]
    if (menuItem) {
      setActiveItem(menuItem)
      const parentSection = menuItemToSection[menuItem]
      if (parentSection) {
        setOpenMenus((prev) => ({
          ...prev,
          [parentSection]: true,
        }))
      }
    }
  }, [location.pathname])

  // Función para alternar colapso en desktop
  const toggleNavbar = () => {
    // Si estamos colapsando la barra, cerramos todos los menús
    if (!isNavbarCollapsed) {
      setOpenMenus({
        VENTAS: false,
        INVENTARIO: false,
        COMPRAS: false,
        TRANSACCION: false,
        RRHH: false,
        ADMINISTRACION: false,
        SOPORTE: false,
      })
    }
    setIsNavbarCollapsed(!isNavbarCollapsed)
  }

  // Toggle de submenús
  const handleToggleMenu = (menu) => {
    if (isNavbarCollapsed) {
      const newOpen = {
        VENTAS: false,
        INVENTARIO: false,
        COMPRAS: false,
        TRANSACCION: false,
        RRHH: false,
        ADMINISTRACION: false,
        SOPORTE: false,
      }
      newOpen[menu] = !openMenus[menu]
      setOpenMenus(newOpen)
    } else {
      setOpenMenus((prev) => ({ ...prev, [menu]: !prev[menu] }))
    }
  }

  // Clic en ítem
  const handleMenuItemClick = (item) => {
    setActiveItem(item)
    // En móvil, cerrar el Drawer tras navegar
    if (isTabletDown) {
      setMobileOpen(false)
    }
  }

  // Función helper para obtener la descripción del menú desde la BD o usar el label por defecto
  const getMenuLabel = (id, defaultLabel) => {
    // Forzar nombre del módulo principal de soporte
    if (id === 'SOPORTE') {
      return 'Soporte';
    }
    // Forzar nombre del submenú de bandejas a TICKETS
    if (id === 'SOPORTE3') {
      return 'TICKETS';
    }
    // Para SOPORTE2, cambiar el nombre según el rol del usuario
    if (id === 'SOPORTE2') {
      const userRole = Cookies.get('rolUsuario') || user?.rolUsuario || '';
      const esAgente = userRole === 'Agente Soporte' || userRole === 'Soporte';
      const esAdmin = userRole === 'Administrador';
      if (esAgente || esAdmin) {
        return menuDescripciones[id] || 'Todos los Tickets';
      }
    }
    return menuDescripciones[id] || defaultLabel
  }

  // Estructura del menú generada dinámicamente usando las descripciones de la BD
  const menuStructure = useMemo(() => ordenarEstructuraMenu([
    {
      id: "DASHBOARD",
      label: getMenuLabel("DASHBOARD", "Inicio"),
      icon: <HomeIcon />,
      path: buildPath(""),
      allowedRoles: ["Administrador", "Vendedor"],
      children: [],
    },
    {
      id: "VENTAS",
      label: getMenuLabel("VENTAS", "Ventas"),
      icon: <MonetizationOnIcon />,
      children: [
        {
          id: "VENTAS1",
          label: getMenuLabel("VENTAS1", "Crear Venta"),
          icon: <ShoppingCartIcon />,
          path: buildPath("gestionGenerarVenta"),
          allowedRoles: ["Administrador", "Vendedor"],
        },
        {
          id: "VENTAS2",
          label: getMenuLabel("VENTAS2", "Ver Ventas"),
          icon: <ProductionQuantityLimitsIcon />,
          path: buildPath("tablaVentas"),
          allowedRoles: ["Administrador", "Vendedor"],
        },
        {
          id: "VENTAS3",
          label: getMenuLabel("VENTAS3", "Crear Presupuesto"),
          icon: <ReceiptIcon />,
          path: buildPath("gestionGenerarPresupuesto"),
          allowedRoles: ["Administrador"],
        },
        {
          id: "VENTAS4",
          label: getMenuLabel("VENTAS4", "Ver Presupuestos"),
          icon: <ProductionQuantityLimitsIcon />,
          path: buildPath("tablaPresupuesto"),
          allowedRoles: ["Administrador"],
        },
        {
          id: "VENTAS5",
          label: getMenuLabel("VENTAS5", "Ver Ventas Detalle"),
          icon: <ProductionQuantityLimitsIcon />,
          path: buildPath("verVentasDetalle"),
          allowedRoles: ["Administrador"],
        },
        {
          id: "VENTAS6",
          label: getMenuLabel("VENTAS6", "Ver Presupuesto Detalle"),
          icon: <ProductionQuantityLimitsIcon />,
          path: buildPath("verPresupuestoDetalle"),
          allowedRoles: ["Administrador"],
        },
        {
          id: "VENTAS7",
          label: getMenuLabel("VENTAS7", "Ver Cliente"),
          icon: <ViewListIcon />,
          path: buildPath("gestionCliente"),
          allowedRoles: ["Administrador", "Vendedor"],
        },
        {
          id: "VENTAS8",
          label: getMenuLabel("VENTAS8", "Lista de Precios"),
          icon: <ViewListIcon />,
          path: buildPath("tablaListaPrecio"),
          allowedRoles: ["Administrador"],
        },
        {
          id: "VENTAS9",
          label: getMenuLabel("VENTAS9", "Pedidos"),
          icon: <ViewListIcon />,
          path: buildPath("tablaVentasEstados"),
          allowedRoles: ["Administrador"],
        },
        {
          id: "VENTAS10",
          label: getMenuLabel("VENTAS10", "Crear Venta (Nuevo)"),
          icon: <ShoppingCartIcon />,
          path: buildPath("ventaCrear"),
          allowedRoles: ["Administrador", "Vendedor"],
        },
        {
          id: "VENTAS11",
          label: getMenuLabel("VENTAS11", "Crear Preventa"),
          icon: <ShoppingCartIcon />,
          path: buildPath("preventaCrear"),
          allowedRoles: ["Administrador", "Vendedor"],
        },
        {
          id: "VENTAS12",
          label: getMenuLabel("VENTAS12", "Ver Preventas"),
          icon: <ProductionQuantityLimitsIcon />,
          path: buildPath("tablaPreventas"),
          allowedRoles: ["Administrador", "Vendedor"],
        },
      ],
    },
    {
      id: "INVENTARIO",
      label: getMenuLabel("INVENTARIO", "Inventario"),
      icon: <FormatListBulletedIcon />,
      children: [
        {
          id: "INVENTARIO1",
          label: getMenuLabel("INVENTARIO1", "Crear Item"),
          icon: <AddCircleIcon />,
          path: buildPath("crearItem"),
          allowedRoles: ["Administrador"],
        },
        {
          id: "INVENTARIO2",
          label: getMenuLabel("INVENTARIO2", "Crear Receta"),
          icon: <ReceiptIcon />,
          path: buildPath("recetaComponent"),
          allowedRoles: ["Administrador"],
        },
        {
          id: "INVENTARIO3",
          label: getMenuLabel("INVENTARIO3", "Stock"),
          icon: <InventoryIcon />,
          path: buildPath("inventario"),
          allowedRoles: ["Administrador", "Vendedor"],
        },
        {
          id: "INVENTARIO4",
          label: getMenuLabel("INVENTARIO4", "Atributos"),
          icon: <ViewListIcon />,
          path: buildPath("Atributos"),
          allowedRoles: ["Administrador"],
        },
        {
          id: "INVENTARIO5",
          label: getMenuLabel("INVENTARIO5", "Recetas"),
          icon: <ViewListIcon />,
          path: buildPath("tablaReceta"),
          allowedRoles: ["Administrador"],
        },
      ],
    },
    {
      id: "COMPRAS",
      label: getMenuLabel("COMPRAS", "Compras"),
      icon: <ShoppingCartIcon />,
      children: [
        {
          id: "COMPRAS1",
          label: getMenuLabel("COMPRAS1", "Crear Compra"),
          icon: <ProductionQuantityLimitsIcon />,
          path: buildPath("crearCompra"),
          allowedRoles: ["Administrador"],
        },
        {
          id: "COMPRAS2",
          label: getMenuLabel("COMPRAS2", "Ver Compras"),
          icon: <ProductionQuantityLimitsIcon />,
          path: buildPath("tablaCompra"),
          allowedRoles: ["Administrador"],
        },
        {
          id: "COMPRAS3",
          label: getMenuLabel("COMPRAS3", "Ver Compra Detalle"),
          icon: <ProductionQuantityLimitsIcon />,
          path: buildPath("verCompraDetalle"),
          allowedRoles: ["Administrador"],
        },
        {
          id: "COMPRAS4",
          label: getMenuLabel("COMPRAS4", "Proveedores"),
          icon: <ViewListIcon />,
          path: buildPath("TablaEntidadProveedor"),
          allowedRoles: ["Administrador"],
        },
        {
          id: "COMPRAS5",
          label: getMenuLabel("COMPRAS5", "Lista Costo"),
          icon: <ViewListIcon />,
          path: buildPath("tablaListaCosto"),
          allowedRoles: ["Administrador"],
        },
      ],
    },
    {
      id: "TRANSACCION",
      label: getMenuLabel("TRANSACCION", "Transacciones"),
      icon: <CurrencyExchangeIcon />,
      children: [
        {
          id: "TRANSACCIONES4",
          label: getMenuLabel("TRANSACCIONES4", "Crear gastos"),
          icon: <PaymentsIcon />,
          path: buildPath("gestionGastosComponent"),
          allowedRoles: ["Administrador"],
        },
        {
          id: "TRANSACCIONES9",
          label: getMenuLabel("TRANSACCIONES9", "Ver Gastos"),
          icon: <PaymentsIcon />,
          path: buildPath("tablaGastosComponent"),
          allowedRoles: ["Administrador"],
        },
        {
          id: "TRANSACCIONES1",
          label: getMenuLabel("TRANSACCIONES1", "Medios de pago"),
          icon: <PaymentsIcon />,
          path: buildPath("gestionMedioPago"),
          allowedRoles: ["Administrador"],
        },
        {
          id: "TRANSACCIONES2",
          label: getMenuLabel("TRANSACCIONES2", "Tipos de transaccion"),
          icon: <RequestQuoteIcon />,
          path: buildPath("gestionTipoTransaccion"),
          allowedRoles: ["Administrador"],
        },
        {
          id: "TRANSACCIONES5",
          label: getMenuLabel("TRANSACCIONES5", "Ver transacciones"),
          icon: <PaymentsIcon />,
          path: buildPath("tablaTransaccionComponent"),
          allowedRoles: ["Administrador"],
        },
        {
          id: "TRANSACCIONES6",
          label: getMenuLabel("TRANSACCIONES6", "Cuentas corrientes"),
          icon: <PaymentsIcon />,
          path: buildPath("tablaCuentaCorriente"),
          allowedRoles: ["Administrador"],
        },
        {
          id: "TRANSACCIONES7",
          label: getMenuLabel("TRANSACCIONES7", "Ventas Afip"),
          icon: <PaymentsIcon />,
          path: buildPath("tablaVentasAfip"),
          allowedRoles: ["Administrador"],
        },
        {
          id: "TRANSACCIONES8",
          label: getMenuLabel("TRANSACCIONES8", "Caja Analitico"),
          icon: <PaymentsIcon />,
          path: buildPath("cajaComponentAnaliticos"),
          allowedRoles: ["Administrador"],
        },
        {
          id: "TRANSACCIONES10",
          label: getMenuLabel("TRANSACCIONES10", "Caja"),
          icon: <PaymentsIcon />,
          path: buildPath("cajaComponent"),
          allowedRoles: ["Administrador"],
        },
      ],
    },
    {
      id: "RRHH",
      label: getMenuLabel("RRHH", "Recursos Humanos"),
      icon: <PersonIcon />,
      children: [
        {
          id: "RRHH1",
          label: getMenuLabel("RRHH1", "Empleados"),
          icon: <BadgeIcon />,
          path: buildPath("rrhh/empleados"),
          allowedRoles: ["Administrador"],
        },
        {
          id: "RRHH2",
          label: getMenuLabel("RRHH2", "Turnos"),
          icon: <ScheduleIcon />,
          path: buildPath("rrhh/turnos"),
          allowedRoles: ["Administrador"],
        },
        {
          id: "RRHH3",
          label: getMenuLabel("RRHH3", "Fichajes"),
          icon: <FingerprintIcon />,
          path: buildPath("rrhh/fichajes"),
          allowedRoles: ["Administrador"],
        },
        {
          id: "RRHH4",
          label: getMenuLabel("RRHH4", "Asistencia"),
          icon: <FactCheckIcon />,
          path: buildPath("rrhh/asistencia"),
          allowedRoles: ["Administrador"],
        },
        {
          id: "RRHH5",
          label: getMenuLabel("RRHH5", "Liquidaciones"),
          icon: <ReceiptLongIcon />,
          path: buildPath("rrhh/liquidaciones"),
          allowedRoles: ["Administrador"],
        },
        {
          id: "RRHH6",
          label: getMenuLabel("RRHH6", "Reportes"),
          icon: <AssessmentIcon />,
          path: buildPath("rrhh/reportes"),
          allowedRoles: ["Administrador"],
        },
      ],
    },
    {
      id: "ADMINISTRACION",
      label: getMenuLabel("ADMINISTRACION", "Administracion"),
      icon: <PermDataSettingIcon />,
      children: [
        {
          id: "ADMINISTRACION1",
          label: getMenuLabel("ADMINISTRACION1", "Monedas"),
          icon: <LocalAtmIcon />,
          path: buildPath("gestionMoneda"),
          allowedRoles: ["Administrador"],
        },
        {
          id: "ADMINISTRACION2",
          label: getMenuLabel("ADMINISTRACION2", "Impuestos"),
          icon: <PaymentsIcon />,
          path: buildPath("Impuestos"),
          allowedRoles: ["Administrador"],
        },
        {
          id: "ADMINISTRACION3",
          label: getMenuLabel("ADMINISTRACION3", "Categoria Gastos"),
          icon: <CardTravelIcon />,
          path: buildPath("gestionCategoria"),
          allowedRoles: ["Administrador"],
        },
        // {
        //   id: "ADMINISTRACION4",
        //   label: getMenuLabel("ADMINISTRACION4", "SubCategoria Transaccion"),
        //   icon: <CardTravelIcon />,
        //   path: buildPath("gestionSubCategoria"),
        //   allowedRoles: ["Administrador"],
        // },
        {
          id: "ADMINISTRACION5",
          label: getMenuLabel("ADMINISTRACION5", "General"),
          icon: <AddLocationAltIcon />,
          path: buildPath("gestionUbicaciones"),
          allowedRoles: ["Administrador"],
        },
        {
          id: "ADMINISTRACION6",
          label: getMenuLabel("ADMINISTRACION6", "Entidad"),
          icon: <PeopleAltIcon />,
          path: buildPath("gestionEntidad"),
          allowedRoles: ["Administrador"],
        },
        {
          id: "ADMINISTRACION7",
          label: getMenuLabel("ADMINISTRACION7", "Usuarios"),
          icon: <PeopleAltIcon />,
          path: buildPath("gestionUsuarios"),
          allowedRoles: ["Administrador"],
        },
        {
          id: "ADMINISTRACION8",
          label: getMenuLabel("ADMINISTRACION8", "Gestion de accesos"),
          icon: <PeopleAltIcon />,
          path: buildPath("gestionarAccesos"),
          allowedRoles: ["Administrador"],
        },
      ],
    },
    {
      id: "SOPORTE",
      label: getMenuLabel("SOPORTE", "Soporte"),
      icon: <SupportAgentIcon />,
      children: [
        {
          id: "SOPORTE1",
          label: getMenuLabel("SOPORTE1", "Crear Ticket"),
          icon: <AddCircleIcon />,
          path: buildPath("soporte/crear"),
          description: "Reportar problema o solicitud",
        },
        {
          id: "SOPORTE2",
          label: getMenuLabel("SOPORTE2", "Mis Tickets"),
          icon: <ViewListIcon />,
          path: buildPath("soporte/tickets"),
          description: "Ver mis tickets",
        },
        {
          id: "SOPORTE3",
          label: getMenuLabel("SOPORTE3", "Bandejas"),
          icon: <FormatListBulletedIcon />,
          path: buildPath("soporte/bandejas"),
          description: "Bandejas de agente",
          allowedRoles: ["Administrador", "Agente Soporte"],
        },
        {
          id: "SOPORTE4",
          label: getMenuLabel("SOPORTE4", "Métricas"),
          icon: <ProductionQuantityLimitsIcon />,
          path: buildPath("soporte/metricas"),
          description: "Estadísticas de soporte",
          allowedRoles: ["Administrador", "Agente Soporte"],
        },
        {
          id: "SOPORTE5",
          label: getMenuLabel("SOPORTE5", "Configuración"),
          icon: <PermDataSettingIcon />,
          path: buildPath("soporte/configuracion"),
          description: "Configurar sistema de soporte",
          allowedRoles: ["Administrador"],
        },
      ],
    },
  ]), [menuDescripciones, buildPath])

  const userAccesses = user?.accesos || []

  // Filtrado: mostrar solo menús y submenús con acceso
  const filteredMenu = menuStructure
    .map((menu) => {
      const filteredChildren =
        menu.children && menu.children.length > 0
          ? menu.children.filter((child) => tieneAcceso(child.id, userAccesses))
          : []
      return { ...menu, children: filteredChildren }
    })
    .filter((menu) => {
      if (menu.children.length === 0) {
        return tieneAcceso(menu.id, userAccesses)
      }
      const tieneAccesoMenuPrincipal = tieneAcceso(menu.id, userAccesses)
      const tieneHijosConAcceso = menu.children.length > 0
      return tieneAccesoMenuPrincipal && tieneHijosConAcceso
    })

  // Contenido común del Drawer (desktop y móvil)
  const DrawerContent = ({ isMobileMode = false }) => (
    <Box
      role="navigation"
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Logo y controles */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: isNavbarCollapsed && !isMobileMode ? "center" : "space-between",
          padding: isNavbarCollapsed && !isMobileMode ? 1 : 2,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}
      >
        {isNavbarCollapsed && !isMobileMode ? (
          <img
            src={lutenteMini || "/placeholder.svg"}
            alt="Small Logo"
            style={{ height: 40, cursor: "pointer" }}
            onClick={toggleNavbar}
          />
        ) : (
          <img
            src={imgLutenteNav || "/placeholder.svg"}
            alt="Logo"
            style={{ height: 40, cursor: "pointer" }}
            onClick={isMobileMode ? () => setMobileOpen(false) : toggleNavbar}
          />
        )}

        {/* Botón de colapso en desktop y botón de cierre en móvil */}
        {!isMobileMode ? (
          !isNavbarCollapsed && (
            <IconButton onClick={toggleNavbar} size="small" aria-label="Colapsar menú">
              <ChevronLeftIcon />
            </IconButton>
          )
        ) : (
          <IconButton onClick={() => setMobileOpen(false)} size="small" aria-label="Cerrar menú">
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      {/* Menú de navegación */}
      <List
        sx={{
          padding: 1,
          flex: 1,
          overflowY: "auto",
          "&::-webkit-scrollbar": { width: "4px" },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: alpha(theme.palette.text.primary, 0.2),
            borderRadius: "4px",
          },
        }}
      >
        {filteredMenu.map((menu) => (
          <React.Fragment key={menu.id}>
            {menu.children.length === 0 ? (
              // Elemento sin hijos
              <ListItem disablePadding sx={{ display: "block", mb: 0.5 }}>
                <ListItemButton
                  component={Link}
                  to={menu.path}
                  onClick={() => handleMenuItemClick(menu.id)}
                  selected={activeItem === menu.id}
                  sx={{
                    minHeight: 48,
                    justifyContent: isNavbarCollapsed && !isMobileMode ? "center" : "initial",
                    px: 2.5,
                    borderRadius: "8px",
                    backgroundColor: activeItem === menu.id ? "rgb(244, 177, 14)" : "transparent",
                    "&:hover": {
                      backgroundColor: alpha(theme.palette.primary.main, 0.05),
                    },
                    "&.Mui-selected": {
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      "&:hover": {
                        backgroundColor: alpha(theme.palette.primary.main, 0.15),
                      },
                    },
                  }}
                >
                  <Tooltip title={isNavbarCollapsed && !isMobileMode ? menu.label : ""} placement="right">
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: isNavbarCollapsed && !isMobileMode ? 0 : 2,
                        justifyContent: "center",
                        color: activeItem === menu.id ? "rgb(244, 177, 14)" : "inherit",
                      }}
                    >
                      {menu.icon}
                    </ListItemIcon>
                  </Tooltip>
                  {(!isNavbarCollapsed || isMobileMode) && (
                    <ListItemText
                      primary={menu.label}
                      primaryTypographyProps={{
                        fontWeight: activeItem === menu.id ? 600 : 400,
                        color: activeItem === menu.id ? "rgb(244, 177, 14)" : "inherit",
                      }}
                    />
                  )}
                </ListItemButton>
              </ListItem>
            ) : (
              // Elemento con submenú
              <>
                <ListItem disablePadding sx={{ display: "block", mb: 0.5 }}>
                  <ListItemButton
                    onClick={() => handleToggleMenu(menu.id)}
                    sx={{
                      minHeight: 48,
                      justifyContent: isNavbarCollapsed && !isMobileMode ? "center" : "initial",
                      px: 2.5,
                      borderRadius: "8px",
                      backgroundColor: openMenus[menu.id] ? alpha(theme.palette.primary.main, 0.05) : "transparent",
                      "&:hover": {
                        backgroundColor: alpha(theme.palette.primary.main, 0.05),
                      },
                    }}
                  >
                    <Tooltip title={isNavbarCollapsed && !isMobileMode ? menu.label : ""} placement="right">
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: isNavbarCollapsed && !isMobileMode ? 0 : 2,
                          justifyContent: "center",
                          color: openMenus[menu.id] ? "rgb(244, 177, 14)" : "inherit",
                        }}
                      >
                        <Badge
                          color="default"
                          variant="dot"
                          invisible={!menu.children.some((child) => activeItem === child.id)}
                        >
                          {menu.icon}
                        </Badge>
                      </ListItemIcon>
                    </Tooltip>
                    {(!isNavbarCollapsed || isMobileMode) && (
                      <>
                        <ListItemText
                          primary={menu.label}
                          primaryTypographyProps={{
                            fontWeight: openMenus[menu.id] ? 600 : 400,
                            color: openMenus[menu.id] ? "rgb(244, 177, 14)" : "inherit",
                          }}
                        />
                        {openMenus[menu.id] ? <ExpandLess /> : <ExpandMore />}
                      </>
                    )}
                  </ListItemButton>
                </ListItem>

                {/* Submenú en desktop expandido */}
                {!isNavbarCollapsed && !isMobileMode && (
                  <Collapse in={openMenus[menu.id]} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {menu.children.map((child) => (
                        <ListItemButton
                          key={`${child.id}-${child.path}`}
                          component={Link}
                          to={child.path}
                          onClick={() => handleMenuItemClick(child.id)}
                          selected={activeItem === child.id}
                          sx={{
                            pl: 4,
                            py: 0.75,
                            minHeight: 40,
                            borderRadius: "8px",
                            ml: 2,
                            mr: 1,
                            mb: 0.5,
                            backgroundColor:
                              activeItem === child.id ? alpha(theme.palette.primary.main, 0.1) : "transparent",
                            "&:hover": {
                              backgroundColor: alpha(theme.palette.primary.main, 0.05),
                            },
                            "&.Mui-selected": {
                              backgroundColor: alpha(theme.palette.primary.main, 0.1),
                              "&:hover": {
                                backgroundColor: alpha(theme.palette.primary.main, 0.15),
                              },
                            },
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 36,
                              color: activeItem === child.id ? "rgb(244, 177, 14)" : "inherit",
                            }}
                          >
                            {child.icon}
                          </ListItemIcon>
                          <ListItemText
                            primary={child.label}
                            primaryTypographyProps={{
                              fontSize: "0.875rem",
                              fontWeight: activeItem === child.id ? 600 : 400,
                              color: activeItem === child.id ? "rgb(244, 177, 14)" : "inherit",
                            }}
                          />
                        </ListItemButton>
                      ))}
                    </List>
                  </Collapse>
                )}

                {/* Menú flotante para modo colapsado (solo desktop) */}
                {!isMobileMode && isNavbarCollapsed && openMenus[menu.id] && (
                  <Box
                    data-dropdown="true"
                    sx={{
                      position: "fixed",
                      left: 72,
                      zIndex: 1300,
                      bgcolor: "background.paper",
                      boxShadow: 4,
                      borderRadius: 1,
                      width: 220,
                      top: 8,
                      maxHeight: "90vh",
                      overflowY: "auto",
                    }}
                  >
                    <List>
                      {menu.children.map((child) => (
                        <ListItemButton
                          key={`${child.id}-${child.path}`}
                          component={Link}
                          to={child.path}
                          onClick={() => handleMenuItemClick(child.id)}
                          selected={activeItem === child.id}
                          sx={{
                            py: 1,
                            "&.Mui-selected": {
                              backgroundColor: alpha(theme.palette.primary.main, 0.1),
                            },
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 36,
                              color: activeItem === child.id ? "rgb(244, 177, 14)" : "inherit",
                            }}
                          >
                            {child.icon}
                          </ListItemIcon>
                          <ListItemText primary={child.label} primaryTypographyProps={{ fontSize: "0.875rem" }} />
                        </ListItemButton>
                      ))}
                    </List>
                  </Box>
                )}

                {/* Submenú en móvil: mostrar dentro del Drawer temporal cuando está abierto */}
                {isMobileMode && (
                  <Collapse in={openMenus[menu.id]} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {menu.children.map((child) => (
                        <ListItemButton
                          key={`${child.id}-${child.path}`}
                          component={Link}
                          to={child.path}
                          onClick={() => handleMenuItemClick(child.id)}
                          selected={activeItem === child.id}
                          sx={{
                            pl: 4,
                            py: 1,
                            minHeight: 44,
                            borderRadius: "8px",
                            ml: 1,
                            mr: 1,
                            mb: 0.5,
                            backgroundColor:
                              activeItem === child.id ? alpha(theme.palette.primary.main, 0.08) : "transparent",
                            "&:hover": {
                              backgroundColor: alpha(theme.palette.primary.main, 0.05),
                            },
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 36,
                              color: activeItem === child.id ? "rgb(244, 177, 14)" : "inherit",
                            }}
                          >
                            {child.icon}
                          </ListItemIcon>
                          <ListItemText primary={child.label} primaryTypographyProps={{ fontSize: "0.95rem" }} />
                        </ListItemButton>
                      ))}
                    </List>
                  </Collapse>
                )}
              </>
            )}
          </React.Fragment>
        ))}
      </List>

      {/* Sección de usuario */}
      <Box
        sx={{
          p: isNavbarCollapsed && !isMobileMode ? 1 : 2,
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          display: "flex",
          flexDirection: isNavbarCollapsed && !isMobileMode ? "column" : "row",
          alignItems: "center",
          justifyContent: isNavbarCollapsed && !isMobileMode ? "center" : "space-between",
        }}
      >
        {user ? (
          <>
            {(!isNavbarCollapsed || isMobileMode) && (
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Avatar
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: theme.palette.primary.main,
                    fontSize: "1rem",
                  }}
                >
                  {user.nombreUsuario?.charAt(0).toUpperCase() || "U"}
                </Avatar>
                <Box sx={{ ml: 1.5, minWidth: 0 }}>
                  <Typography variant="subtitle2" noWrap sx={{ fontWeight: 600 }}>
                    {user.nombreUsuario}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {user.rolUsuario || "Usuario"}
                  </Typography>
                </Box>
              </Box>
            )}

            <Tooltip title="Cerrar sesión">
              <IconButton
                onClick={logout}
                size="small"
                sx={{
                  ml: isNavbarCollapsed && !isMobileMode ? 0 : 1,
                  mt: isNavbarCollapsed && !isMobileMode ? 1 : 0,
                  color: theme.palette.error.main,
                  bgcolor: alpha(theme.palette.error.main, 0.1),
                  "&:hover": { bgcolor: alpha(theme.palette.error.main, 0.2) },
                }}
                aria-label="Cerrar sesión"
              >
                <LogoutIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        ) : (
          <Button
            component={Link}
            to="/login"
            variant="contained"
            fullWidth
            sx={{ borderRadius: "8px", textTransform: "none" }}
          >
            {isNavbarCollapsed && !isMobileMode ? <PersonIcon /> : "Iniciar sesión"}
          </Button>
        )}
      </Box>
    </Box>
  )

  // Render: en desktop, Drawer permanente; en móvil, Drawer temporal + FAB
  return (
    <>
      {isTabletDown ? (
        <>
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            ModalProps={{ keepMounted: true }}
            PaperProps={{
              "aria-label": "Menú de navegación",
            }}
            sx={{
              zIndex: theme.zIndex.drawer + 2,
              "& .MuiDrawer-paper": {
                width: "min(86vw, 360px)",
                boxSizing: "border-box",
                backgroundColor: theme.palette.mode === "light" ? "#ffffff" : "#1e1e1e",
              },
            }}
          >
            <DrawerContent isMobileMode />
          </Drawer>
        </>
      ) : (
        <Drawer
          variant="permanent"
          open={!isNavbarCollapsed}
          sx={{
            width: isNavbarCollapsed ? 72 : drawerWidth,
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width: isNavbarCollapsed ? 72 : drawerWidth,
              boxSizing: "border-box",
              backgroundColor: theme.palette.mode === "light" ? "#ffffff" : "#1e1e1e",
              boxShadow: "0 0 10px rgba(0, 0, 0, 0.1)",
              transition: theme.transitions.create(["width"], {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
              overflowX: "hidden",
            },
          }}
        >
          <DrawerContent />
        </Drawer>
      )}

      {/* Zona de activación para swipe gesture en mobile */}
      {isTabletDown && (
        <Box
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          sx={{
            position: 'fixed',
            left: 0,
            top: 0,
            width: '30px',
            height: '100vh',
            zIndex: theme.zIndex.drawer - 1,
            touchAction: 'pan-y', // Permite scroll vertical, previene back horizontal
            pointerEvents: mobileOpen ? 'none' : 'auto', // Desactivar cuando navbar está abierto
          }}
        />
      )}
    </>
  )
}

export default NavBar
